import SelectInput, {
    SelectOption,
} from '@components/common/SelectInput/SelectInput';
import DatePicker from '@components/common/DatePicker';
import TextInput from '@components/common/TextInput';
import { farmingHist, FarmlandAddReq } from '@/api/growth/farmland/type';
import { useState } from 'react';
import { formatDateString, parseDate } from '@utils/Utils';

interface FertilizerMngProps {
    formData: FarmlandAddReq;
    onInputChange: (field: string, value: farmingHist[]) => void;
}

const FertilizerMng = ({ formData, onInputChange }: FertilizerMngProps) => {
    const [farmingHists, setFarmingHists] = useState<farmingHist[]>(
        formData.farmingHistList?.filter(h => h.category === 'F') || [
            {
                category: 'F',
                workDt: '',
                iamt: '',
                iame: '',
                chcod: '',
                chamt: '',
                chme: '',
                erad: '',
                emax: '',
                emin: '',
                ewind: '',
                ename: '',
                fmcd: '',
                facd: '',
                famn: '',
                famp: '',
                famk: '',
                rcod: '',
                ramt: '',
                rdep: '',
                timpl: '',
                tdep: '',
            },
        ],
    );

    const handleFarmingHistChange = (
        index: number,
        field: keyof farmingHist,
        value: string | number,
    ) => {
        const updated = [...farmingHists];
        updated[index] = { ...updated[index], [field]: value };
        setFarmingHists(updated);
        // ✅ 부모에 즉시 업데이트 (다른 카테고리 데이터 유지)
        updateParentFarmingHistList(updated);
    };

    const updateParentFarmingHistList = (fertilizerData: farmingHist[]) => {
        // formData의 기존 데이터 중 시비관리(F)가 아닌 것들 유지
        const otherData =
            formData.farmingHistList?.filter(h => h.category !== 'F') || [];
        const allData = [...otherData, ...fertilizerData];
        onInputChange('farmingHistList', allData);
    };

    const addFarmingHist = () => {
        const newRecord: farmingHist = {
            category: 'F',
            workDt: '',
            iamt: '',
            iame: '',
            chcod: '',
            chamt: '',
            chme: '',
            erad: '',
            emax: '',
            emin: '',
            ewind: '',
            ename: '',
            fmcd: '',
            facd: '',
            famn: '',
            famp: '',
            famk: '',
            rcod: '',
            ramt: '',
            rdep: '',
            timpl: '',
            tdep: '',
        };
        const updated = [...farmingHists, newRecord];
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const removeFarmingHist = (index: number) => {
        const updated = farmingHists.filter((_, i) => i !== index);
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const fertilizerTypeOptions: SelectOption[] = [
        {
            value: 'FE005',
            label: '요소(46% N) - 질소 공급 기본 비료(추천)',
        },
        {
            value: 'FE006',
            label: 'DAP(18-46-0) - 질소+인 공급',
        },
        {
            value: 'FE007',
            label: 'MAP(11-52-0) - 인 공급 위주',
        },
        {
            value: 'FE013',
            label: '단 super phosphate - 인 + 황 공급',
        },
        {
            value: 'FE014',
            label: '고농도 인 비료 - 인 46%',
        },
        {
            value: 'FE016',
            label: '염화칼륨(KCl) - 칼륨 비료 기본',
        },
        {
            value: 'FE017',
            label: '질산칼륨 - N + K 복합',
        },
        {
            value: 'FE018',
            label: '황산칼륨(SOP) - Cl 민감 작물용',
        },
        {
            value: 'FE019',
            label: '대립 요소 비료 - 심층 시비에 많이 사용',
        },
        {
            value: 'FE042',
            label: '암모니아 처리된 SP - 벼 재배에서 자주 사용',
        },
    ];

    const fertilizationMethodOptions: SelectOption[] = [
        {
            value: 'AP001',
            label: '표면 살포(혼입 없음) - 가장 기본',
        },
        {
            value: 'AP002',
            label: '표면 살포 후 혼입 - 경운기·괴비 혼입',
        },
        {
            value: 'AP003',
            label: '지표면 밴드 시비 - 줄 시비',
        },
        {
            value: 'AP004',
            label: '지하 밴드 시비 - 조파·줄 비료',
        },
        {
            value: 'AP007',
            label: '심층 구멍 시비 - 벼 비료 깊이 투입',
        },
        {
            value: 'AP008',
            label: '종자 처리 시비 - 식재·파종과 함께',
        },
        {
            value: 'AP009',
            label: '비료 주입 - 액체 비료 사용',
        },
        {
            value: 'AP011',
            label: '침수 토양 표면 살포 - 벼 재배 대표 방식',
        },
        {
            value: 'AP018',
            label: '포화토양 밴드 시비 - 벼 시비에 많이 사용',
        },
        {
            value: 'AP019',
            label: '요소 대립 심층 투입 - 벼 NH₃ 손실 방지 효과',
        },
    ];

    return (
        <div className="fertilizer-management">
            <div className="row mb-3">
                <div className="col-12 text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addFarmingHist}
                    >
                        <i className="ri-add-line me-1"></i>
                        시비 추가
                    </button>
                </div>
            </div>

            {farmingHists.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-3">
                        <thead className="text-center">
                            <tr>
                                <th style={{ width: '15%' }}>일자 선택</th>
                                <th style={{ width: '20%' }}>비료종류</th>
                                <th style={{ width: '20%' }}>시비방법</th>
                                <th style={{ width: '7%' }}>
                                    질소
                                    <div className="fs-9">(N) (kg/ha)</div>
                                </th>
                                <th style={{ width: '7%' }}>
                                    인산
                                    <div className="fs-9">(P₂O₅) (kg/ha)</div>
                                </th>
                                <th style={{ width: '7%' }}>
                                    칼리
                                    <div className="fs-9">(K₂O) (kg/ha)</div>
                                </th>
                                <th style={{ width: '1%' }}>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farmingHists.map((record, index) => (
                                <tr key={index}>
                                    <td>
                                        <DatePicker
                                            selectedDate={parseDate(
                                                record.workDt || '',
                                            )}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'workDt',
                                                    formatDateString(value),
                                                )
                                            }
                                            size="sm"
                                            placeholder="날짜를 선택해주세요"
                                            maxDate={undefined}
                                            showYearDropdown={false}
                                            showMonthDropdown={false}
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={fertilizerTypeOptions}
                                            value={record.fmcd || ''}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'fmcd',
                                                    value,
                                                )
                                            }
                                            placeholder="선택해주세요"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={fertilizationMethodOptions}
                                            value={record.facd || ''}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'facd',
                                                    value,
                                                )
                                            }
                                            placeholder="선택해주세요"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.famn
                                                    ? record.famn.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'famn',
                                                    value,
                                                )
                                            }
                                            placeholder="질소 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={false}
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.famp
                                                    ? record.famp.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'famp',
                                                    value,
                                                )
                                            }
                                            placeholder="인산 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={false}
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.famk
                                                    ? record.famk.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'famk',
                                                    value,
                                                )
                                            }
                                            placeholder="칼리 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={false}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                removeFarmingHist(index)
                                            }
                                            title="삭제"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {farmingHists.length === 0 && (
                <div className="alert alert-info text-center py-4">
                    <p className="mb-0">
                        <i className="ri-information-line me-2"></i>
                        무기질비료(MF) 관련 내용을 입력해주세요. 추가 버튼을
                        눌러 시비 기록을 추가할 수 있습니다.
                    </p>
                </div>
            )}
        </div>
    );
};

export default FertilizerMng;
